/**
 * PATH: packages/job-hub/src/jobs/qa/qaReportsApi.ts
 * FILE_ID: JOBHUB-QA-REPORTS-API-001
 * PURPOSE: QA report lifecycle and download endpoint client.
 */
import type { JobHubHttpClient } from '../jobHubDataAdapter'
import { normalizeList } from './qaApiHelpers'
import type { QaApi, QaItpLibraryDetail, QaItpLibraryEntry, QaItpLibraryList, QaReport } from './qaTypes'

type ReportsApi = Pick<
  QaApi,
  'listReports' | 'listItpLibrary' | 'createItpLibraryEntry' | 'getItpLibraryEntry' | 'generateReport' | 'renderItpPdf' | 'listItpFromMailboxes' | 'sendItpEmail' | 'itpOnedriveStatus' | 'downloadReport' | 'downloadReportFile' | 'reportDownloadUrl'
>

const ITP_SLOW_TIMEOUT_MS = 120_000

export function createQaReportsApi(
  client: JobHubHttpClient,
  headers: Record<string, string> | undefined,
): ReportsApi {
  return {
    async listItpLibrary(jobId) {
      const response = await client.get('/api/qa/itp-library/', {
        params: { job_id: jobId },
        headers,
      })
      return response.data as QaItpLibraryList
    },
    async createItpLibraryEntry(body) {
      const response = await client.post('/api/qa/itp-library/', body, { headers })
      return response.data as QaItpLibraryEntry
    },
    async getItpLibraryEntry(id) {
      const response = await client.get(`/api/qa/itp-library/${id}/`, { headers })
      return response.data as QaItpLibraryDetail
    },
    async listReports(inspectionId) {
      const response = await client.get('/api/qa/reports/', {
        params: { inspection_id: inspectionId },
        headers,
      })
      return normalizeList<QaReport>(response.data)
    },
    async generateReport(inspectionId, reportType, reportName, scope) {
      const response = await client.post(
        '/api/qa/reports/generate/',
        {
          inspection_id: inspectionId,
          report_type: reportType,
          ...(reportName ? { report_name: reportName } : {}),
          // Omitted rather than sent empty: the backend reads a missing scope
          // as "the whole inspection", and {} would have to mean the same
          // thing in two places instead of one.
          ...(scope ? { scope } : {}),
        },
        { headers },
      )
      return response.data as QaReport
    },
    async renderItpPdf(inspectionId, html, placements) {
      const response = await client.post(
        '/api/qa/reports/render-itp/',
        {
          inspection_id: inspectionId,
          html,
          ...(placements ? { placements } : {}),
        },
        { headers, responseType: 'blob', timeout: ITP_SLOW_TIMEOUT_MS },
      )
      return response.data as Blob
    },
    async itpOnedriveStatus(inspectionId, fromAccountId) {
      const response = await client.get(
        `/api/qa/inspections/${inspectionId}/itp-onedrive-status/`,
        { params: { from_account_id: fromAccountId }, headers },
      )
      return response.data as { ready: boolean; authorize_url: string; admin_consent_url?: string }
    },
    async listItpFromMailboxes(inspectionId) {
      const response = await client.get(
        `/api/qa/inspections/${inspectionId}/from-mailboxes/`,
        { headers },
      )
      return response.data as {
        accounts: Array<{
          id: number
          email_address: string
          display_name?: string
          provider?: string
          owner_user_id?: number | null
        }>
      }
    },
    async sendItpEmail(inspectionId, payload) {
      const response = await client.post(
        `/api/qa/inspections/${inspectionId}/send-email/`,
        payload,
        { headers, timeout: ITP_SLOW_TIMEOUT_MS },
      )
      return response.data
    },
    async downloadReport(reportId) {
      const response = await client.get(`/api/qa/reports/${reportId}/download/`, {
        headers,
        responseType: 'blob',
      })
      return response.data as Blob
    },
    async downloadReportFile(reportId) {
      const response = await client.get(`/api/qa/reports/${reportId}/download/`, {
        headers,
        params: { as: 'file' },
        responseType: 'blob',
      })
      return response.data as Blob
    },
    reportDownloadUrl(reportId) {
      return `/api/qa/reports/${reportId}/download/`
    },
  }
}
